import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  AppleOutlined,
  CheckCircleOutlined,
  CodeOutlined,
  GoogleOutlined,
  KeyOutlined,
  LinkedinOutlined,
  LockOutlined,
  MailOutlined,
  RocketOutlined,
  TeamOutlined,
  UserOutlined,
  WindowsOutlined,
} from "@ant-design/icons";
import { Alert, Button, Card, Input, Progress, Space, Typography } from "antd";
import AuthBrand from "components/auth/AuthBrand";
import AuthShell from "components/auth/AuthShell";
import { useAuth } from "contexts/AuthContext";
import paths from "routes/paths";
import { OLIVEEB_COLORS } from "workspaces/colors";

const { Paragraph, Text, Title } = Typography;

const starterStorageKey = "ovacusInitialWorkspaceDraft";

const optionIconStyle = {
  fontSize: 18,
};

const questions = [
  {
    key: "useCase",
    eyebrow: "Your path",
    title: "How will you use Ovacus first?",
    deck: "This helps us shape your signup around the account you actually need.",
    colors: {
      eyebrow: OLIVEEB_COLORS.oliveebBlue,
      title: OLIVEEB_COLORS.oliveebDarkBlue,
      deck: OLIVEEB_COLORS.oliveebBlue,
      accent: OLIVEEB_COLORS.oliveebBlue,
      soft: OLIVEEB_COLORS.oliveebBlueSoft,
    },
    options: [
      { value: "personal_delivery", label: "For My Work", caption: "Plan and track your own projects and validation work.", icon: <UserOutlined style={optionIconStyle} /> },
      { value: "team_delivery", label: "For a Team", caption: "Create a workspace for projects, tasks, testing, and members.", icon: <TeamOutlined style={optionIconStyle} /> },
    ],
  },
  {
    key: "hiringType",
    eyebrow: "Identity",
    title: "What best describes this setup?",
    deck: "Choose whether this workspace is personal or for an organization.",
    showIf: (answers) => answers.useCase === "team_delivery",
    colors: {
      eyebrow: OLIVEEB_COLORS.oliveebGreen,
      title: OLIVEEB_COLORS.oliveebDarkGreen,
      deck: OLIVEEB_COLORS.oliveebGreen,
      accent: OLIVEEB_COLORS.oliveebGreen,
      soft: OLIVEEB_COLORS.oliveebGreenSoft,
    },
    options: [
      { value: "individual", label: "An Individual", caption: "I am organizing delivery on my own.", icon: <UserOutlined style={optionIconStyle} /> },
      { value: "organization", label: "Organization/Team", caption: "I am setting this up for a company or delivery team.", icon: <TeamOutlined style={optionIconStyle} /> },
    ],
  },
  {
    key: "workspaceName",
    eyebrow: "Workspace",
    title: "What is the organization or team name?",
    deck: "Use the name that should appear in your Ovacus workspace.",
    showIf: (answers) => answers.useCase === "team_delivery" && answers.hiringType === "organization",
    colors: {
      eyebrow: OLIVEEB_COLORS.oliveebGreen,
      title: OLIVEEB_COLORS.oliveebDarkGreen,
      deck: OLIVEEB_COLORS.oliveebGreen,
      accent: OLIVEEB_COLORS.oliveebGreen,
      soft: OLIVEEB_COLORS.oliveebGreenSoft,
    },
    input: {
      placeholder: "Engineering Delivery Workspace",
      maxLength: 80,
    },
  },
  {
    key: "deliveryFocus",
    eyebrow: "Focus",
    title: "What do you want Ovacus to help with first?",
    deck: "Select every focus area that should shape your first experience.",
    colors: {
      eyebrow: OLIVEEB_COLORS.oliveebOrange,
      title: OLIVEEB_COLORS.oliveebDarkRed,
      deck: OLIVEEB_COLORS.oliveebOrange,
      accent: OLIVEEB_COLORS.oliveebOrange,
      soft: OLIVEEB_COLORS.goldSoft,
    },
    multiple: true,
    options: [
      { value: "engineering_projects", label: "Engineering projects", caption: "Plan, assign, track, and ship software work.", icon: <CodeOutlined style={optionIconStyle} /> },
      { value: "agile_delivery", label: "Agile delivery", caption: "Coordinate sprints, iterations, and team flow.", icon: <RocketOutlined style={optionIconStyle} /> },
      { value: "testing_validation", label: "Testing & validation", caption: "Keep validation signals close to delivery.", icon: <CheckCircleOutlined style={optionIconStyle} /> },
    ],
  },
  {
    key: "credentialMethod",
    eyebrow: "Login",
    title: "How would you like to create your login?",
    deck: "Choose a username and password, or continue with a social account.",
    colors: {
      eyebrow: OLIVEEB_COLORS.oliveebGreen,
      title: OLIVEEB_COLORS.oliveebDarkGreen,
      deck: OLIVEEB_COLORS.oliveebBlue,
      accent: OLIVEEB_COLORS.oliveebGreen,
      soft: OLIVEEB_COLORS.oliveebGreenSoft,
    },
    options: [
      { value: "username_password", label: "Username and password", caption: "Create your Ovacus login with an email username and password.", icon: <KeyOutlined style={optionIconStyle} /> },
      {
        value: "social_login",
        label: "Social login",
        caption: "Use Google, Apple, Microsoft, or LinkedIn.",
        icon: (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <GoogleOutlined style={optionIconStyle} />
            <AppleOutlined style={optionIconStyle} />
            <WindowsOutlined style={optionIconStyle} />
            <LinkedinOutlined style={optionIconStyle} />
          </span>
        ),
      },
    ],
  },
  {
    key: "loginCredentials",
    eyebrow: "Username and password",
    title: "Create your username and password",
    deck: "Use an email username and a password for your Ovacus login.",
    showIf: (answers) => answers.credentialMethod === "username_password",
    colors: {
      eyebrow: OLIVEEB_COLORS.oliveebGreen,
      title: OLIVEEB_COLORS.oliveebDarkGreen,
      deck: OLIVEEB_COLORS.oliveebBlue,
      accent: OLIVEEB_COLORS.oliveebGreen,
      soft: OLIVEEB_COLORS.oliveebGreenSoft,
    },
    credentialsForm: true,
  },
];

const initialAnswers = {
  email: "",
  password: "",
  confirmPassword: "",
  useCase: "",
  hiringType: "",
  usageType: "",
  workspaceName: "",
  deliveryFocus: [],
  credentialMethod: "",
  socialProvider: "",
};

const onboardingStyles = `
  .ovacus-onboarding-card {
    border-radius: 28px;
    background: linear-gradient(155deg, rgba(255,255,255,0.98) 0%, rgba(246,255,249,0.96) 58%, rgba(255,253,235,0.94) 100%);
    box-shadow: 0 26px 72px rgba(0, 82, 64, 0.14), inset 0 1px 0 rgba(255,255,255,0.95);
    border: 1px solid ${OLIVEEB_COLORS.oliveebGreenSoft};
    overflow: hidden;
  }
  .ovacus-onboarding-option {
    width: 100%;
    height: auto;
    min-height: 76px;
    padding: 12px 14px;
    border-radius: 20px;
    text-align: left;
    white-space: normal;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    box-shadow: 0 12px 24px rgba(16,147,87,0.08);
  }
  .ovacus-onboarding-input {
    height: 54px;
    border-radius: 999px !important;
    border-color: ${OLIVEEB_COLORS.oliveebGreenSoft} !important;
    font-size: 17px;
    font-weight: 800;
    color: ${OLIVEEB_COLORS.oliveebDarkBlue};
    box-shadow: 0 14px 28px rgba(16,147,87,0.10);
  }
  .ovacus-onboarding-input.ant-input-affix-wrapper,
  .ovacus-onboarding-input.ant-input-password {
    border-radius: 999px !important;
    overflow: hidden;
  }
  .ovacus-onboarding-input .ant-input {
    font-size: 17px;
    font-weight: 800;
    color: ${OLIVEEB_COLORS.oliveebDarkBlue};
  }
  .ovacus-onboarding-input:focus,
  .ovacus-onboarding-input-focused {
    border-color: ${OLIVEEB_COLORS.oliveebGreen} !important;
    box-shadow: 0 0 0 4px rgba(39,174,48,0.12), 0 14px 28px rgba(16,147,87,0.10) !important;
  }
`;

const getVisibleQuestions = (answers) =>
  questions.filter((question) => !question.showIf || question.showIf(answers));

const normalizeMultiValue = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
};

const formatLabelValue = (value) =>
  Array.isArray(value) ? value.filter(Boolean).join(", ") : value;

const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

const readStarterDraft = () => {
  if (typeof window === "undefined") return null;
  try {
    return JSON.parse(window.localStorage.getItem(starterStorageKey) || "null");
  } catch {
    return null;
  }
};

const hiringTypeFromUsageType = (usageType) => {
  if (usageType === "team" || usageType === "enterprise") return "organization";
  if (usageType === "solo") return "individual";
  return "";
};

const getMissingWorkspaceNotice = (setupReason) => {
  if (setupReason === "first-time") {
    return {
      message: "Account setup needs to be completed",
      description: "We could not find your first workspace yet. Finish these steps to create it and continue into Ovacus.",
    };
  }

  if (setupReason === "repair") {
    return {
      message: "Workspace access needs to be restored",
      description: "We could not find an active workspace for this account. Complete these steps to create one and restore access.",
    };
  }

  return null;
};

const ProductOnboardingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();
  const [stepIndex, setStepIndex] = useState(0);
  const [resumeApplied, setResumeApplied] = useState(false);
  const [resumeStepKey] = useState(() => {
    const draft = location.state?.onboardingDraft || readStarterDraft();
    return draft?.currentStepKey || "";
  });
  const [answers, setAnswers] = useState(() => {
    const draft = location.state?.onboardingDraft || readStarterDraft();
    if (!draft) return initialAnswers;
    return {
      email: draft.email || "",
      password: "",
      confirmPassword: "",
      useCase: draft.useCase || (draft.usageType === "team" || draft.usageType === "enterprise" ? "team_delivery" : draft.usageType ? "personal_delivery" : ""),
      hiringType: draft.hiringType || hiringTypeFromUsageType(draft.usageType),
      usageType: draft.usageType || "",
      workspaceName: draft.workspaceName || "",
      deliveryFocus: normalizeMultiValue(draft.deliveryFocus),
      credentialMethod: draft.credentialMethod || "",
      socialProvider: draft.socialProvider || "",
    };
  });
  const visibleQuestions = useMemo(() => getVisibleQuestions(answers), [answers]);
  const currentQuestion = visibleQuestions[Math.min(stepIndex, visibleQuestions.length - 1)];
  const currentColors = currentQuestion.colors || {
    eyebrow: OLIVEEB_COLORS.oliveebGreen,
    title: OLIVEEB_COLORS.oliveebDarkGreen,
    deck: OLIVEEB_COLORS.oliveebBlue,
    accent: OLIVEEB_COLORS.oliveebGreen,
    soft: OLIVEEB_COLORS.oliveebGreenSoft,
  };
  const currentValue = answers[currentQuestion.key];
  const missingWorkspaceNotice =
    location.state?.source === "missing-workspace"
      ? getMissingWorkspaceNotice(location.state?.setupReason)
      : null;
  const isLastStep = stepIndex === visibleQuestions.length - 1;
  const canContinue = (() => {
    if (currentQuestion.key === "credentialMethod") {
      return Boolean(answers.credentialMethod);
    }
    if (currentQuestion.key === "loginCredentials") {
      return (
        isValidEmail(answers.email) &&
        String(answers.password || "").length >= 8 &&
        answers.password === answers.confirmPassword
      );
    }
    if (currentQuestion.multiple) {
      const visibleValues = (currentQuestion.options || []).map((option) => option.value);
      return normalizeMultiValue(currentValue).some((value) => visibleValues.includes(value));
    }
    if (currentQuestion.options) {
      return currentQuestion.options.some((option) => option.value === currentValue);
    }
    return String(currentValue || "").trim().length > 0;
  })();
  const progress = Math.round(((stepIndex + 1) / visibleQuestions.length) * 100);

  const selectedLabels = useMemo(
    () =>
      visibleQuestions.reduce((summary, question) => {
        const value = answers[question.key];
        if (question.credentialsForm) return summary;
        if (question.multiple) {
          const labels = normalizeMultiValue(value)
            .map((item) => question.options?.find((option) => option.value === item)?.label)
            .filter(Boolean);
          if (labels.length) summary[question.key] = labels;
          return summary;
        }
        if (!value) return summary;
        const visibleOption = question.options?.find((option) => option.value === value);
        if (question.options && !visibleOption) return summary;
        summary[question.key] = visibleOption?.label || value;
        return summary;
      }, {}),
    [answers, visibleQuestions],
  );

  const buildOnboardingDraft = (finalAnswers, overrides = {}) => {
    const isTeam = finalAnswers.useCase === "team_delivery";
    const isOrganization = isTeam && finalAnswers.hiringType === "organization";
    const resolvedUsageType = isOrganization ? "team" : "solo";
    const workspaceAccountType = isOrganization ? "ORGANIZATION" : "PERSONAL";
    const workspaceName = isOrganization
      ? finalAnswers.workspaceName.trim()
      : isTeam
        ? "Personal team delivery workspace"
        : "Personal project workspace";
    const organizationName = isOrganization ? finalAnswers.workspaceName.trim() : "";

    return {
      source: "product-get-started",
      capturedAt: new Date().toISOString(),
      currentStepKey: overrides.currentStepKey || "",
      email: finalAnswers.email.trim(),
      useCase: finalAnswers.useCase,
      useCaseLabel: selectedLabels.useCase,
      hiringType: finalAnswers.hiringType,
      hiringTypeLabel: selectedLabels.hiringType,
      usageType: resolvedUsageType,
      usageTypeLabel: isTeam ? "For a Team" : "For My Work",
      workspaceName,
      deliveryFocus: normalizeMultiValue(finalAnswers.deliveryFocus),
      deliveryFocusLabel: selectedLabels.deliveryFocus || [],
      role: isTeam ? "engineering_manager" : "founder",
      roleOther: "",
      roleLabel: isTeam ? "Team delivery lead" : "Individual project owner",
      credentialMethod: finalAnswers.credentialMethod,
      socialProvider: finalAnswers.socialProvider,
      workspaceCreation: {
        accountType: workspaceAccountType,
        workspaceUse: "PROJECT_MANAGEMENT",
        name: workspaceName,
        organizationName,
        onboardingIntent: "PROJECT_MANAGEMENT",
        product: "OLIVEEB_OVACUS_SERVICE",
      },
    };
  };

  useEffect(() => {
    if (resumeApplied) return;
    if (!resumeStepKey) {
      setResumeApplied(true);
      return;
    }
    const resumedIndex = visibleQuestions.findIndex((question) => question.key === resumeStepKey);
    if (resumedIndex >= 0) {
      setStepIndex(resumedIndex);
    }
    setResumeApplied(true);
  }, [resumeApplied, resumeStepKey, visibleQuestions]);

  useEffect(() => {
    if (!answers.useCase) return;
    try {
      window.localStorage.setItem(
        starterStorageKey,
        JSON.stringify(buildOnboardingDraft(answers, { currentStepKey: currentQuestion?.key })),
      );
    } catch {
      // Local resume is best-effort.
    }
  }, [answers, currentQuestion?.key]);

  const updateAnswer = (key, value) => {
    setAnswers((current) => ({
      ...current,
      [key]: value,
      ...(key === "useCase" && current.useCase !== value
        ? {
            hiringType: "",
            usageType: "",
            workspaceName: "",
            deliveryFocus: [],
            credentialMethod: "",
            socialProvider: "",
            email: "",
            password: "",
            confirmPassword: "",
          }
        : {}),
      ...(key === "hiringType" && current.hiringType !== value
        ? {
            workspaceName: "",
          }
        : {}),
      ...(key === "credentialMethod" && value !== "social_login"
        ? { socialProvider: "" }
        : {}),
      ...(key === "credentialMethod" && value !== "username_password"
        ? { email: "", password: "", confirmPassword: "" }
        : {}),
    }));
  };

  const toggleAnswer = (key, value) => {
    setAnswers((current) => {
      const selected = normalizeMultiValue(current[key]);
      return {
        ...current,
        [key]: selected.includes(value)
          ? selected.filter((item) => item !== value)
          : [...selected, value],
      };
    });
  };

  const finish = (overrides = {}) => {
    const finalAnswers = { ...answers, ...overrides };
    const onboardingDraft = buildOnboardingDraft(finalAnswers, { currentStepKey: "signup" });

    try {
      window.localStorage.setItem(starterStorageKey, JSON.stringify(onboardingDraft));
    } catch {
      // Route state still carries the draft when storage is unavailable.
    }

    navigate(paths.simpleSignup, {
      state: {
        ...(location.state || {}),
        onboardingDraft,
        signupCredentialMethod: finalAnswers.credentialMethod === "social_login" ? "social" : "form",
        signupSocialProvider: finalAnswers.credentialMethod === "social_login" ? finalAnswers.socialProvider : "",
        signupCredentials: finalAnswers.credentialMethod === "username_password"
          ? {
              email: finalAnswers.email.trim(),
              password: finalAnswers.password,
            }
          : null,
      },
    });
  };

  const selectOption = (key, value) => {
    updateAnswer(key, value);
    if (key === "credentialMethod") {
      if (value === "social_login") {
        finish({ credentialMethod: value, socialProvider: "" });
        return;
      }
      setStepIndex((index) => Math.min(index + 1, visibleQuestions.length));
    }
  };

  const goNext = () => {
    if (!canContinue) return;
    if (isLastStep) {
      finish();
      return;
    }
    setStepIndex((index) => Math.min(index + 1, visibleQuestions.length - 1));
  };

  const goBack = () => {
    if (stepIndex === 0) {
      navigate("/", { state: location.state });
      return;
    }
    setStepIndex((index) => Math.max(index - 1, 0));
  };

  const resetOnboardingFlow = () => {
    try {
      window.localStorage.removeItem(starterStorageKey);
    } catch {
      // Local storage cleanup is best-effort.
    }
    setAnswers(initialAnswers);
    setStepIndex(0);
    setResumeApplied(true);
    navigate(paths.getStarted, { replace: true, state: null });
  };

  const goToSignIn = () => {
    try {
      window.localStorage.removeItem(starterStorageKey);
    } catch {
      // Local storage cleanup is best-effort.
    }
    navigate(paths.simpleLogin, { state: null });
  };

  const goToSignOut = () => {
    try {
      window.localStorage.removeItem(starterStorageKey);
    } catch {
      // Local storage cleanup is best-effort.
    }
    logout(false, paths.simpleLogin);
  };

  return (
    <AuthShell mode="center">
      <style>{onboardingStyles}</style>
      <Card
        variant="borderless"
        className="ovacus-onboarding-card"
        style={{ width: "min(100%, 620px)" }}
        styles={{ body: { padding: 24 } }}
      >
        <Space direction="vertical" size={18} style={{ width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={goBack}
              shape="circle"
              style={{
                color: OLIVEEB_COLORS.oliveebBlue,
                borderColor: OLIVEEB_COLORS.oliveebBlue,
                background: OLIVEEB_COLORS.white,
                boxShadow: "0 10px 22px rgba(0,87,184,0.12)",
              }}
            />
            <div
              style={{
                padding: "7px 14px",
                borderRadius: 24,
                background: OLIVEEB_COLORS.white,
                border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
                boxShadow: "0 12px 24px rgba(39,174,48,0.14)",
              }}
            >
              <AuthBrand centered size={78} />
            </div>
            <Space size={8}>
              <Button
                type="link"
                onClick={resetOnboardingFlow}
                style={{ color: OLIVEEB_COLORS.textMuted, fontWeight: 900, paddingInline: 0 }}
              >
                Reset
              </Button>
              <Button
                type="link"
                onClick={isAuthenticated ? goToSignOut : goToSignIn}
                style={{ color: OLIVEEB_COLORS.oliveebBlue, fontWeight: 900, paddingInline: 0 }}
              >
                {isAuthenticated ? "Sign out" : "Sign in"}
              </Button>
            </Space>
          </div>

          <Progress
            percent={progress}
            showInfo={false}
            strokeColor={currentColors.accent}
            trailColor={currentColors.soft}
          />

          {missingWorkspaceNotice ? (
            <Alert
              showIcon
              type={location.state?.setupReason === "repair" ? "warning" : "info"}
              message={missingWorkspaceNotice.message}
              description={missingWorkspaceNotice.description}
              style={{
                borderRadius: 18,
                borderColor:
                  location.state?.setupReason === "repair"
                    ? OLIVEEB_COLORS.goldBorder
                    : OLIVEEB_COLORS.oliveebBlueSoft,
                background:
                  location.state?.setupReason === "repair"
                    ? "linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)"
                    : "linear-gradient(135deg, #F2F8FF 0%, #FFFFFF 100%)",
              }}
            />
          ) : null}

          <Space direction="vertical" size={6} style={{ width: "100%", textAlign: "center" }}>
            <Text
              style={{
                color: currentColors.eyebrow,
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                fontSize: 11,
              }}
            >
              {currentQuestion.eyebrow}
            </Text>
            <Title
              level={2}
              style={{
                color: currentColors.title,
                margin: 0,
                fontWeight: 900,
                lineHeight: 1.12,
              }}
            >
              {currentQuestion.title}
            </Title>
            <Paragraph
              style={{
                margin: 0,
                color: currentColors.deck,
                fontSize: 15,
                lineHeight: 1.6,
              }}
            >
              {currentQuestion.deck}
            </Paragraph>
          </Space>

          {currentQuestion.credentialsForm ? (
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <Input
                className="ovacus-onboarding-input"
                value={answers.email}
                placeholder="Email username"
                autoComplete="email"
                inputMode="email"
                autoFocus
                prefix={<MailOutlined style={{ color: currentColors.accent }} />}
                onChange={(event) => updateAnswer("email", event.target.value)}
                onPressEnter={goNext}
              />
              <Input.Password
                className="ovacus-onboarding-input"
                value={answers.password}
                placeholder="Password"
                autoComplete="new-password"
                prefix={<LockOutlined style={{ color: currentColors.accent }} />}
                onChange={(event) => updateAnswer("password", event.target.value)}
                onPressEnter={goNext}
              />
              <Input.Password
                className="ovacus-onboarding-input"
                value={answers.confirmPassword}
                placeholder="Confirm password"
                autoComplete="new-password"
                prefix={<LockOutlined style={{ color: currentColors.accent }} />}
                onChange={(event) => updateAnswer("confirmPassword", event.target.value)}
                onPressEnter={goNext}
              />
            </Space>
          ) : currentQuestion.options ? (
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              {currentQuestion.options.map((option) => {
                const selected = currentQuestion.multiple
                  ? normalizeMultiValue(currentValue).includes(option.value)
                  : currentValue === option.value;
                return (
                  <Button
                    key={option.value}
                    className="ovacus-onboarding-option"
                    icon={option.icon}
                    onClick={() =>
                      currentQuestion.multiple
                        ? toggleAnswer(currentQuestion.key, option.value)
                        : selectOption(currentQuestion.key, option.value)
                    }
                    style={{
                      borderColor: selected
                        ? currentColors.accent
                        : currentColors.soft,
                      background: selected
                        ? `linear-gradient(135deg, ${currentColors.soft} 0%, #FFFFFF 100%)`
                        : OLIVEEB_COLORS.white,
                      color: selected
                        ? currentColors.title
                        : OLIVEEB_COLORS.oliveebDarkBlue,
                    }}
                  >
                    <Space direction="vertical" size={2}>
                      <Text style={{ color: "inherit", fontWeight: 900, fontSize: 16 }}>
                        {option.label}
                      </Text>
                      <Text style={{ color: "inherit", fontSize: 12, opacity: 0.82 }}>
                        {option.caption}
                      </Text>
                    </Space>
                    {selected ? (
                      <CheckCircleOutlined
                        style={{
                          marginLeft: "auto",
                          color: currentColors.accent,
                          fontSize: 18,
                        }}
                      />
                    ) : null}
                  </Button>
                );
              })}
            </Space>
          ) : (
            <Input
              className="ovacus-onboarding-input"
              value={formatLabelValue(currentValue)}
              maxLength={currentQuestion.input.maxLength}
              placeholder={currentQuestion.input.placeholder}
              autoFocus
              onChange={(event) => updateAnswer(currentQuestion.key, event.target.value)}
              onPressEnter={goNext}
            />
          )}

          <Button
            type="primary"
            size="large"
            block
            disabled={!canContinue}
            icon={isLastStep ? <CheckCircleOutlined /> : <ArrowRightOutlined />}
            onClick={goNext}
            style={{
              height: 50,
              borderRadius: 999,
              fontWeight: 900,
              fontSize: 16,
              background: canContinue
                ? `linear-gradient(135deg, ${currentColors.accent} 0%, ${OLIVEEB_COLORS.oliveebGreen} 100%)`
                : currentColors.soft,
              borderColor: canContinue
                ? currentColors.accent
                : currentColors.soft,
              boxShadow: canContinue
                ? "0 14px 28px rgba(16,147,87,0.22)"
                : "none",
            }}
          >
            {isLastStep ? "Continue to signup" : "Continue"}
          </Button>
        </Space>
      </Card>
    </AuthShell>
  );
};

export default ProductOnboardingPage;
